import React from 'react';

/**
 * FrostCard — shared frosted glass card with a viewport-FIXED
 * boots-on-pavers (installer + lawn) image behind it. Because the image
 * is fixed to the viewport, scrolling reveals different parts of the
 * same image through each card — creating depth and continuity.
 *
 * The image is intentionally FAINT: a heavy forest→obsidian gradient
 * sits on the same element via the CSS `background-image` stack, so
 * both layers share `background-attachment: fixed` and stay in lockstep
 * as the user scrolls. The boots photo reads as a textural whisper, not
 * a primary visual.
 *
 * Used on Contact (info + form cards) and Home → Our Process step cards.
 */
export default function FrostCard({
  children,
  padding = 'p-6',
  className = '',
  testId,
  variant = 'dark',
}) {
  const isMuted = variant === 'muted';
  const isSage  = variant === 'sage';
  // Sage variant: a solid muted-green card (no fixed boots photo behind it)
  // — used as a quiet bookend either side of the "dark" card that anchors
  // the section's fixed background image. Built from the brand greens
  // (Accent #6A8F3E mixed toward Off-White #E7E9E4) so it stays palette-compliant.
  if (isSage) {
    return (
      <div
        data-testid={testId}
        className={`relative rounded-2xl overflow-hidden ${padding} ${className}`}
        style={{
          // Pale white-green wash: built from off-white (#E7E9E4) tinted
          // very slightly toward the accent green so the card reads as
          // "white with a green whisper" rather than "muted sage".
          background: 'linear-gradient(150deg, #EEF2E6 0%, #E1E8D5 100%)',
          border: '1px solid rgba(49,79,43,0.18)',
          boxShadow: '0 30px 70px -30px rgba(49,79,43,0.22), inset 0 1px 0 rgba(255,255,255,0.6)',
        }}
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.30), rgba(106,143,62,0.0))',
          }}
        />
        <div className="relative">{children}</div>
      </div>
    );
  }
  return (
    <div
      data-testid={testId}
      className={`relative rounded-2xl overflow-hidden ${padding} ${className}`}
      style={{
        backgroundImage: isMuted
          ? 'linear-gradient(135deg, rgba(231,233,228,0.92), rgba(214,221,205,0.88)), url(/textures/process-boots.jpg)'
          : 'linear-gradient(135deg, rgba(49,79,43,0.94), rgba(22,42,30,0.92)), url(/textures/process-boots.jpg)',
        backgroundSize: 'cover, cover',
        backgroundAttachment: 'fixed, fixed',
        backgroundPosition: 'center, center 45%',
        backgroundRepeat: 'no-repeat, no-repeat',
        border: isMuted ? '1px solid rgba(49,79,43,0.18)' : '1px solid rgba(231,233,228,0.18)',
        boxShadow: isMuted
          ? '0 30px 70px -30px rgba(49,79,43,0.20), inset 0 1px 0 rgba(255,255,255,0.5)'
          : '0 30px 70px -30px rgba(22,42,30,0.40), inset 0 1px 0 rgba(231,233,228,0.15)',
      }}
    >
      {/* Subtle frosted sheen on top for the "glass" feel */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background: isMuted
            ? 'linear-gradient(135deg, rgba(255,255,255,0.20), rgba(231,233,228,0.0))'
            : 'linear-gradient(135deg, rgba(231,233,228,0.06), rgba(22,42,30,0.0))',
        }}
      />
      <div className="relative">{children}</div>
    </div>
  );
}
